import React, { Fragment } from 'react';
import Styled, { useTheme } from 'styled-components';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import ProgramCollapsibleTitle from '@components/ProgramCollapsibleTitle';
import {
  DaySection,
  ProgressStatus,
} from '@ecosystems/programs/single/components';
import PhaseProgramVideoCard from '@organisms/cards/ProgramVideoCard';
import ProgramDocumentCard from '@organisms/cards/ProgramDocumentCard';
import ProgramSurveyCard from '@organisms/cards/ProgramSurveyCard';
import ProgramTextCard from '@organisms/cards/ProgramTextCard';
import { Box } from '@components/layout/Grid';
import { GenericProgram, ProgramCategory } from '@gql/generated';
import { Instructor, WeeklyProgramWeekFragmentFragment } from '@gql/generated';
import { ProgramLiveEventCard } from '@organisms/cards/ProgramLiveEventCard';

const getVideoDescription = video => {
  if (video?.content) {
    const { content } = video;
    return content?.shortDescription || content?.longDescription;
  }
  return '';
};

const PhaseDescription = Styled.p`
  max-width: 940px;
  padding: 20px;
  background-color: #F7F7F7;
  border-radius: 3px;

  &:empty {
    display: none;
  }
`;

const Subheader = Styled.p`
  font-size: 16px;
  font-weight: 600;
  
  ::first-letter {
    text-transform: uppercase;
  }
`;

export const VIDEO_KINDS = {
  introduction: t`metadata.introduction`,
  exercise: t`metadata.exercise`,
  lecture: t`metadata.lecture`,
  bonus: t`metadata.bonus`,
  question: t`metadata.question`,
  workshop: t`metadata.workshop`,
  skills: t`metadata.skills`,
  talk: t`metadata.talk`,
};

type Props = {
  index: number;
  programSlug: string;
  week: WeeklyProgramWeekFragmentFragment;
  programLanguage: string;
  userHasProgramAccess: boolean;
  isOpen: boolean;
  onOpen(): void;
  onClose(): void;
  programCategory: GenericProgram['category'];
};

const ProgramWeekCollapsable = (props: Props) => {
  const {
    index,
    programSlug,
    week,
    programLanguage,
    userHasProgramAccess,
    isOpen,
    onOpen,
    onClose,
    programCategory,
  } = props;
  const theme = useTheme();

  return (
    <ProgramCollapsibleTitle
      key={week.id}
      isOpen={isOpen}
      isDone={week.finished}
      appearance={programCategory}
      title={isHighlighted => (
        <>
          <ProgressStatus
            active={week.finished}
            isHighlighted={isHighlighted}
            appearance={programCategory}
            theme={theme}
          />
          {week.label || (
            <Trans
              id="weeklyProgram.week.number"
              values={{ number: index + 1 }}
            />
          )}
        </>
      )}
      videosCount={week['videosCount']}
      progressTooltipText={<Trans id="weeklyProgram.week_progress.text" />}
      showProgressTooltip={true}
      showProgress={
        (week.progress > 0 || week.progress == 0) && week.locked !== true
      }
      progress={week.progress}
      videosDuration={week['videosDuration']}
      onOpen={onOpen}
      onClose={onClose}
      data-testid={props['data-testid']}
    >
      <>
        <PhaseDescription
          data-testid="ProgramWeekCollapsable--Description"
          dangerouslySetInnerHTML={{ __html: week.description }}
        />
        <Box as={DaySection} isCurrentDay={week['isCurrent']} py={15} pl={0}>
          {week.elements.map((element, i) => {
            let card = null;
            if (element.__typename === 'ProgramDocument') {
              const { slug, locked } = element;
              card = (
                <ProgramDocumentCard
                  key={slug}
                  tags={[]}
                  locked={locked}
                  category={programCategory}
                  redirectTo={`/${programLanguage}/${programCategory}/${programSlug}/${week.slug}/${slug}`}
                  documentElement={element}
                />
              );
            } else if (element.__typename === 'ProgramSurveyElement') {
              const { slug, locked } = element;
              if (locked) {
                return null;
              }
              card = (
                <ProgramSurveyCard
                  key={slug}
                  tags={[]}
                  locked={locked}
                  category={programCategory}
                  redirectTo={`/${programLanguage}/${programCategory}/${programSlug}/${week.slug}/${slug}`}
                  surveyElement={element}
                />
              );
            } else if (element.__typename === 'ProgramPartElement') {
              card = (
                <ProgramTextCard
                  key={element.slug}
                  tags={[]}
                  locked={element.locked}
                  category={programCategory}
                  redirectTo={`/${programLanguage}/${programCategory}/${programSlug}/${week.slug}/${element.slug}`}
                  textElement={element}
                />
              );
            } else if (element.__typename === 'ProgramLiveEvent') {
              return (
                <ProgramLiveEventCard
                  key={element.slug}
                  element={element}
                  weekSlug={week.slug}
                  programSlug={programSlug}
                  category={programCategory}
                />
              );
            } else if (element.__typename === 'ProgramVideo') {
              const { title, slug } = element;
              const {
                customDescription,
                videoKind,
                video,
                locked: videoLocked,
              } = element;

              const locked = videoLocked || !userHasProgramAccess;

              // we used videoTags previously for summer program
              let videoTags;
              if (locked) {
                videoTags = [t`metadata.preview`];
              } else if (VIDEO_KINDS.hasOwnProperty(videoKind)) {
                videoTags = [VIDEO_KINDS[videoKind]];
              }
              card = (
                <PhaseProgramVideoCard
                  tags={videoTags}
                  locked={locked}
                  redirectTo={`/${programLanguage}/${programCategory}/${programSlug}/${week.slug}/${slug}`}
                  video={{
                    id: video.id,
                    identifier: video.identifier,
                    title: title || video.title,
                    description:
                      customDescription || getVideoDescription(video),
                    thumbnailUrl: video.assets.thumbnailUrl,
                    kind: video.kind,
                    flavor: video.taxonomies?.flavor?.name,
                    publishedAt: video.meta?.publishedAt,
                    duration: video.meta.minutes,
                    cursorAt: video.cursorAt,
                    genreName: video.taxonomies?.genre?.name,
                    genreSlug: video.taxonomies?.genre?.slug,
                    instructors: video?.taxonomies?.instructors as Instructor[],
                  }}
                  programCategory={programCategory}
                  disableActions={
                    programCategory === ProgramCategory['Courses']
                  }
                />
              );
            } else {
              return null;
            }
            return (
              <Fragment key={element['slug'] || i}>
                {element['subheader'] ? (
                  <Subheader>{element['subheader']}</Subheader>
                ) : null}
                {card}
              </Fragment>
            );
          })}
        </Box>
      </>
    </ProgramCollapsibleTitle>
  );
};

export default ProgramWeekCollapsable;
